import { useDropzone } from 'react-dropzone'
import {
  Box, InputLabel, Typography, useMediaQuery,
} from '@mui/material'
import { useState } from 'react'
import { filesize } from 'filesize'
import arrowCircleUp from '../../images/arrowCircleUp.svg'
import DeleteIcon from '../../images/Delete.svg'
import UseTranslation from '../../hooks/UseTranslation'
import { ReactComponent as TeamSnap } from '../../images/TeamSnap.svg'
import { getIconForExtension } from '../../utils/helpers'

const OfoqAttachmentInput = ({
  onFilesChange,
  onFileAdd,
  onFileRemove,
  name,
  required,
  maxCount = 5,
  fileTypes = [],
  maxSize = 5 * 1024 * 1024, // default 5MB
  defaultValue,
  hideBrowse = false,
  customStyles,
  subBody,
}) => {
  const [selectedFiles, setSelectedFiles] = useState(defaultValue || [])
  const [errorMessage, setErrorMessage] = useState('')
  const { t } = UseTranslation()
  const isXsScreen = useMediaQuery((theme) => theme.breakpoints.only('xs'))
  const splitName = name?.split('_')?.join(' ')

  const validateFile = (file) => {
    const extension = file.name.split('.').pop().toLowerCase()
    if (maxCount && selectedFiles.length >= maxCount) {
      return `${t?.["You can't upload more than"]} ${maxCount}`
    }
    if (fileTypes?.length && !fileTypes.includes(extension)) {
      return t?.['This file type is not Supported']
    }
    if (file.size > maxSize) {
      return t?.['File size can\'t exceed 2MB']
    }
    if (file.size === 0) {
      return t?.['File size can\'t be Zero']
    }

    if (selectedFiles.some((f) => f.name === file.name)) {
      return `${t?.File} ${file.name} ${t?.['already exists.']}`
    }

    return null
  }

  const onDrop = async (acceptedFiles) => {
    const validFiles = []

    // eslint-disable-next-line no-restricted-syntax
    for (const file of acceptedFiles) {
      const validationError = validateFile(file)
      if (validationError) {
        setErrorMessage(validationError)
        return
      }

      // eslint-disable-next-line no-await-in-loop
      const uploadedFile = onFileAdd ? await onFileAdd?.(file) : file
      if (!uploadedFile) {
        setErrorMessage(t?.['File upload failed.'])
        return
      }

      validFiles.push(uploadedFile)
    }

    if (validFiles.length > 0) {
      const updatedFiles = [...selectedFiles, ...validFiles]
      setSelectedFiles(updatedFiles)
      onFilesChange?.(updatedFiles)
      setErrorMessage('')
    }
  }

  const handleDelete = async (file) => {
    const updatedFiles = selectedFiles.filter((f) => f !== file)

    if (onFileRemove) {
      const isRemoved = await onFileRemove(file)
      if (!isRemoved) return
    }

    setSelectedFiles(updatedFiles)
    onFilesChange?.(updatedFiles)
    setErrorMessage('')
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  return (
    <Box sx={[styles.attachmentComponent, customStyles]}>
     {name && <InputLabel
        htmlFor={name}
        variant="heading6"
        sx={styles.formInputLabel}
      >
        {splitName}
        {required && <TeamSnap style={styles.snapIcon} data-testid="teamsnap-icon" />}
      </InputLabel>}
      {!hideBrowse && (
        <Box
          {...getRootProps()}
           sx={styles.root}
        >
          <input {...getInputProps()} />
          <Box sx={styles.attachmentContainer}>
            <Box
              component="img"
              src={arrowCircleUp}
              alt="Upload"
            />
            {!isXsScreen ? (
              <Typography variant="heading6" sx={styles.bodyContainer}>
                {isDragActive ? (
                  t?.['Drop your File Here ...']
                ) : (
                  <Box variant="heading6" sx={styles.header}>
                    {t?.['Drag your File Here or Click to']}
                    <Typography variant="heading6" sx={styles.contentLink}>{t?.Browse}</Typography>
                  </Box>
                )}
                <Typography variant="heading6" sx={styles.subBody}>{subBody}</Typography>
              </Typography>
            ) : (
              <Typography variant="heading6" sx={styles.header}>
                {t?.['Click to']}
                <Typography variant="heading6" sx={styles.contentLink}>{t?.Browse}</Typography>
              </Typography>
            )}
            {errorMessage && (
              <Typography sx={styles.errorMessage} variant="heading9">
                {errorMessage}
              </Typography>
            )}
            {required && !selectedFiles.length && (
              <Typography sx={styles.errorMessage} variant="heading9">
                {t?.['This field is required.']}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      {selectedFiles.length > 0 && (
        <Box sx={styles.selectedFilesContainer}>
          {selectedFiles.map((file) => (
            <Box
              key={file.name}
              sx={styles.files}
            >
              <Box sx={styles.iconWithText}>
                {getIconForExtension(file.name)}
                <Typography
                  variant="heading9"
                  sx={styles.fileName}
                >
                  {file.name}
                  <Typography variant="heading9" sx={styles.fileSize}>
                    {filesize(file.size)}
                  </Typography>
                </Typography>
              </Box>
              <Box
                onClick={() => handleDelete(file)}
                component="img"
                src={DeleteIcon}
                alt="Delete"
                sx={styles.deleteIcon}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

const styles = {
  attachmentComponent: {
    paddingTop: { xs: '10px', sm: '30px' },
  },
  root: {
    padding: { xs: '10px', sm: '20px' },
    borderWidth: '2px',
    borderStyle: 'dashed',
    borderColor: 'primary.main',
    borderRadius: '12px',
    background: '#F2FFFA',
    marginTop: '10px',
    cursor: 'pointer',
  },
  dimmedOverlay: {
    opacity: 0.4,
    pointerEvents: 'none',
  },
  attachmentContainer: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    '& img': {
      width: { xs: '40px', sm: '100px' },
      height: { xs: '40px', sm: '100px' },
    },
  },
  bodyContainer: {
    marginTop: '14px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
  },
  subBody: {
    marginTop: '7px',
    textAlign: 'center',
    color: 'text.hint',
  },
  contentLink: {
    color: 'primary.main',
    marginLeft: '3px',
  },
  selectedFilesContainer: {
    maxHeight: { xs: '100px', sm: '70px' },
    overflowY: 'auto',
    marginBlock: '10px',
  },
  files: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '11px 10px',
  },
  iconWithText: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    '& svg': {
      width: { xs: '25px', sm: '30px', lg: '35px' },
      height: { xs: '25px', sm: '30px', lg: '35px' },
    },
  },
  errorMessage: {
    color: 'red',
    marginTop: '5px',
  },
  fileName: {
    display: 'flex',
    alignItems: 'start',
    flexDirection: 'column',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fileSize: {
    color: 'text.hint',
  },
  deleteIcon: {
    cursor: 'pointer',
    width: { xs: '25px', sm: '30px', lg: '35px' },
    height: { xs: '25px', sm: '30px', lg: '35px' },
  },
  snapIcon: {
    marginInlineStart: '8px',
  },
  formInputLabel: {
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(0,0) scale(1)',
    textTransform: 'capitalize',
    color: 'text.primary',
    fontSize: '14px',
    marginBottom: '8px',
  },
}

export default OfoqAttachmentInput
